//dependencies
import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ICustomQuestionGetResponse } from "@performyard/models";

// components
import Button from "../../../../../../../components/Button";
import SelectInput, { Result } from "../../../../../../../components/Inputs/SelectInput";
import { RadioButtons } from "../../../../../../../components/Inputs/SelectionOptions/RadioButtons";
import Modal from "../../../../../../../components/Modal";
import Spacer from "../../../../../../../components/Spacer";
import Typography from "../../../../../../../components/Text";
import { TextEditor } from "../../../../../../../components/TextEditor";
import Tooltip from "../../../../../../../components/Tooltip";

// hooks
import {
	useCreateEngagementSurveyCustomQuestion,
	useMutateEngagementSurveyCustomQuestion,
} from "../../../../../../../api/hooks/defs/engagement-survey-custom-questions";
import useForm from "../../../../../../../hooks/useForm";
import useToast from "../../../../../../../hooks/useToast";

// utils
import { getQuestionURL } from "./utils";

// css
import css from "./CreateOrEditCustomSurveyQuestionModal.module.scss";

const QUESTION_TYPES: Result[] = [
	{
		label: "Rating Scale (Likert-5)",
		value: "likert5",
	},
];

const MAX_QUESTION_LENGTH = 128;

const formSchema = z.object({
	scale: z.enum(["likert5", "text"]),
	question: z.string().min(0).max(MAX_QUESTION_LENGTH),
	enabled: z.boolean(),
	factor: z.enum(["Custom"]),
	subfactor: z.string().min(0).max(MAX_QUESTION_LENGTH).nullable(),
});

type CustomQuestionCreateRequest = z.infer<typeof formSchema>;

const DEFAULT_QUESTION: CustomQuestionCreateRequest = {
	scale: "likert5",
	question: "",
	enabled: true,
	factor: "Custom",
	subfactor: null,
};

type CreateOrEditCustomSurveyQuestionModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSave: () => void;
	selectedQuestion?: ICustomQuestionGetResponse;
};

const CreateOrEditCustomSurveyQuestionModal = ({
	isOpen = false,
	onClose,
	onSave,
	selectedQuestion,
}: CreateOrEditCustomSurveyQuestionModalProps) => {
	const toast = useToast();
	const { state, setInitialState, updateForm, validate, resetForm, hasUnsavedChanges } = useForm({
		schema: formSchema,
	});

	if (!QUESTION_TYPES.find((type) => type.value === "text")) {
		QUESTION_TYPES.push({
			label: "Comment Box",
			value: "text",
		});
	}

	const editor = useRef<ReactElement | null>();
	const validation = useMemo(() => validate(), [state]);
	const [attemptedSubmit, setAttemptedSubmit] = useState(false);

	const { mutate: createQuestion, isPending: isCreateLoading } = useCreateEngagementSurveyCustomQuestion(
		onSave,
		(response) => {
			const errors = response?.data?.errors || [];
			toast(errors[0].message || "Failed to create custom question", {
				type: "error",
				size: "large",
			});
		}
	);
	const { mutate: editQuestion, isPending: isEditLoading } = useMutateEngagementSurveyCustomQuestion(onSave, () =>
		toast("Failed to get Custom Questions", {
			type: "error",
			size: "large",
		})
	);

	const initializeModal = (question: CustomQuestionCreateRequest) => {
		setInitialState({
			scale: question.scale,
			question: question.question,
			enabled: question.enabled,
			factor: question.factor,
			subfactor: question.subfactor,
		});
		updateForm({
			scale: question.scale,
			question: question.question,
			enabled: question.enabled,
			factor: question.factor,
			subfactor: question.subfactor,
		});

		editor.current = (
			<TextEditor
				label="Question Text"
				labelProps={{ size: "paragraph-3", weight: "semi-bold" }}
				maxLength={MAX_QUESTION_LENGTH}
				showCharacterCounter
				initialValue={question.question}
				onChange={(value) => updateForm({ question: value })}
				placeholder="Your question text here"
				minHeight={96}
				className={css.textEditor}
			/>
		);
	};

	useEffect(() => {
		if (isOpen) {
			initializeModal(selectedQuestion || DEFAULT_QUESTION);
		} else {
			resetForm();
			editor.current = null;
		}

		return () => {
			editor.current = null;
			setAttemptedSubmit(false);
		};
	}, [isOpen]);

	const handleCreateQuestion = () => {
		setAttemptedSubmit(true);
		if (validation.isValid) {
			if (selectedQuestion) {
				editQuestion({
					_id: selectedQuestion._id,
					question: state.question,
					scale: state.scale,
					enabled: state.enabled,
					factor: state.factor,
					subfactor: state.subfactor,
				});
			} else
				createQuestion({
					question: state.question,
					scale: state.scale,
					enabled: state.enabled,
					factor: state.factor,
					subfactor: state.subfactor,
				});
		}
	};

	const modalContent = (
		<div className={css.createModalContainer}>
			<Typography size="sub-heading" weight="semi-bold" className={css.header}>
				Custom Question
			</Typography>
			<Spacer height={3} />
			<SelectInput
				disabled={selectedQuestion !== undefined}
				label="Question Type"
				labelProps={{ size: "paragraph-3", weight: "semi-bold" }}
				results={QUESTION_TYPES}
				value={{
					value: state.scale,
					label: QUESTION_TYPES.find((type) => type.value === state.scale)?.label || "",
				}}
				onChange={({ value }) => updateForm({ scale: value })}
			/>
			<Spacer height={2} />
			<div>{editor.current}</div>
			{!validation?.isValid && validation?.errors?.question && (hasUnsavedChanges || attemptedSubmit) && (
				<div>
					<Spacer height={1} />
					<Typography className={css.errorText} size="paragraph-3" color="red-5" weight="regular">
						<FontAwesomeIcon icon={faCircleInfo} />
						{state.question.length > MAX_QUESTION_LENGTH &&
							`Question must be less than ${MAX_QUESTION_LENGTH} characters`}
					</Typography>
				</div>
			)}
			<Spacer height={1} />
			<Button
				size="micro"
				disabled={!state.question || !state.scale || !validation.isValid}
				onClick={() => {
					const url = getQuestionURL(state as ICustomQuestionGetResponse);
					window.open(url, "_blank");
				}}
				icon={faMagnifyingGlass}
				iconPosition="left"
				className={css.exampleButton}
			>
				Question Preview
			</Button>
			<Spacer height={2} />
			<div className={css.quesitonStatusTitle}>
				<Typography size="paragraph-3" weight="semi-bold">
					Question Status
				</Typography>
				<span className={css.questionStatusTooltip}>
					<Tooltip
						message={"Control if a question is available for use in upcoming surveys."}
						placement="right"
					>
						<FontAwesomeIcon icon={faCircleInfo} />
					</Tooltip>
				</span>
			</div>
			<Spacer height={1} />
			<RadioButtons
				options={[
					{
						label: "Enabled",
						value: "enabled",
					},
					{
						label: "Disabled",
						value: "disabled",
					},
				]}
				value={{
					value: state.enabled ? "enabled" : "disabled",
					label: state.enabled ? "Enabled" : "Disabled",
				}}
				onChange={({ value }) => updateForm({ enabled: value === "enabled" })}
			/>
			<Spacer height={3} />
			<div className={css.actionButtons}>
				<Button onClick={onClose}>Discard</Button>
				<Button
					color="green"
					isRequesting={isCreateLoading || isEditLoading}
					onClick={handleCreateQuestion}
					disabled={!state.question || !state.scale || !validation.isValid}
				>
					{selectedQuestion ? "Save Question" : "Create Question"}
				</Button>
			</div>
			<Spacer height={2} />
		</div>
	);

	return isOpen ? <Modal content={modalContent} handleClose={onClose} /> : null;
};

export default CreateOrEditCustomSurveyQuestionModal;
